import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { fetchExternalBusy } from "@/lib/services/externalBusyService";
import type { BusyBlock } from "@/lib/calendar/externalBusy";

/** One week's busy blocks. Record writes invalidate it with the week's appointments (recordCaches.ts). */
export const externalBusyKey = (weekStart: string) => ["externalBusy", weekStart];

/** The seven days of the week that starts on `weekStart` (a Monday, YYYY-MM-DD). */
export function weekDays(weekStart: string): string[] {
  const monday = DateTime.fromISO(weekStart, { zone: "utc" });
  if (!monday.isValid) return [];
  return Array.from({ length: 7 }, (_, i) => monday.plus({ days: i }).toISODate()!);
}

/**
 * Busy blocks held outside Boona for the journal's week (lib/calendar/externalBusy.ts), fetched
 * beside the appointments and cached like them: one entry per week, so the day view moves through
 * a week without refetching, the same stale time and the same minute poll. Nothing waits on it —
 * the journal draws `data ?? []`, so a failed fetch only means no bands.
 */
export function useExternalBusyWeek(weekStart: string | null | undefined) {
  return useQuery<BusyBlock[]>({
    queryKey: externalBusyKey(weekStart ?? ""),
    queryFn: () => fetchExternalBusy(weekDays(weekStart!)),
    enabled: !!weekStart,
    staleTime: 5 * 60 * 1000, // 5 minutes, like the week's appointments
    refetchInterval: 60_000, // the minute poll the appointments use (doc 07 Phase B)
  });
}
